<script module lang="ts">
  export type BulkTenderStatus =
    | 'pending'
    | 'success'
    | 'noDocuments'
    | 'not_found'
    | 'conflict'
    | 'failed'
    | 'cancelled'

  export interface BulkTenderResult {
    id: number
    title: string
    status: BulkTenderStatus
    phase?: string | null
    retryAt?: string | null
  }
</script>

<script lang="ts">
  import { push } from 'svelte-spa-router'
  import { tStore } from '../../../i18n'
  import StatusBadge from '../../../components/StatusBadge.svelte'

  let {
    results,
    onClose,
    onCancel,
  }: {
    results: BulkTenderResult[] | null
    onClose: () => void
    onCancel: () => void
  } = $props()

  // Drives the Cancel button's disabled state — nothing to cancel once every
  // row in this batch has left 'pending' (running).
  let pendingCount = $derived(results?.filter((r) => r.status === 'pending').length ?? 0)

  function badgeTone(status: BulkTenderStatus): 'neutral' | 'success' | 'warning' | 'danger' {
    switch (status) {
      case 'pending':
        return 'neutral'
      case 'success':
        return 'success'
      case 'noDocuments':
        return 'warning'
      case 'conflict':
        return 'warning'
      case 'cancelled':
        return 'neutral'
      default:
        return 'danger'
    }
  }

  function badgeLabel(status: BulkTenderStatus): string {
    return $tStore(`views.dashboard.bulkStatus.${status}`)
  }

  function viewAiHistory() {
    push('/ai-history')
  }
</script>

{#if results}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="dialog-backdrop" onclick={onClose}>
    <div
      class="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-ai-results-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
    >
      <h3 id="bulk-ai-results-title">{$tStore('views.dashboard.bulkAiResultsTitle')}</h3>

      <ul class="results-list">
        {#each results as r (r.id)}
          <li class="results-list__row">
            <span class="results-list__title">{r.title}</span>
            <span role="status">
              <StatusBadge
                label={r.status === 'pending' && r.phase
                  ? $tStore('views.dashboard.aiPhase.' + r.phase)
                  : badgeLabel(r.status)}
                tone={badgeTone(r.status)}
              />
              {#if r.status === 'pending' && r.retryAt}
                <small>{new Date(r.retryAt).toLocaleTimeString()}</small>
              {/if}
            </span>
          </li>
        {/each}
      </ul>

      <div class="dialog__actions">
        <button type="button" class="btn btn--secondary" onclick={viewAiHistory}>
          {$tStore('views.dashboard.bulkViewHistory')}
        </button>
        <button
          type="button"
          class="btn btn--secondary"
          onclick={onCancel}
          disabled={pendingCount === 0}
          title={$tStore('views.dashboard.bulkCancelTooltip')}
        >
          {$tStore('views.dashboard.bulkCancel')}
        </button>
        <button type="button" class="btn btn--secondary" onclick={onClose}>
          {$tStore('common.close')}
        </button>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  .dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .dialog {
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-lg;
    box-shadow: v.$shadow-lg;
    padding: v.$space-lg;
    max-width: 32rem;
    width: calc(100% - 2rem);
    max-height: calc(100vh - 4rem);
    display: flex;
    flex-direction: column;

    h3 {
      margin: 0 0 v.$space-sm;
      font-size: 1.125rem;
    }
  }

  .results-list {
    list-style: none;
    margin: 0 0 v.$space-lg;
    padding: 0;
    overflow-y: auto;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
  }

  .results-list__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-md;
    padding: v.$space-sm v.$space-md;

    & + & {
      border-top: 1px solid v.$color-border;
    }
  }

  .results-list__title {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }
</style>
